import { useEffect, useState } from "react";
import { Loader2, UserCog } from "lucide-react";
import { Button, cn } from "@nexus/ui";
import { useTranslation } from "react-i18next";
import type { UserItem } from "@/features/identity-hub/api/identity-hub.api";
import { useUpdateUser } from "@/features/identity-hub/hooks/useIdentityHub";
import { DialogIconHeader } from "./shared/dialog/DialogIconHeader";

const DISPLAY_NAME_MAX = 100;
const DISPLAY_NAME_MIN = 2;
const CHAR_COUNTER_THRESHOLD = 80;

function validateDisplayName(value: string, t: (k: string) => string): string | null {
  if (value.length === 0) return t("iam.editProfile.displayNameRequired");
  if (value.length > DISPLAY_NAME_MAX) return t("iam.editProfile.displayNameTooLong");
  if (value.trim().length === 0) return t("iam.editProfile.displayNameBlank");
  if (value.trim().length < DISPLAY_NAME_MIN) return t("iam.editProfile.displayNameTooShort");
  return null;
}

export interface PersonProfileFormProps {
  user: UserItem;
  activeTenantId: string | null;
  variant?: "dialog" | "tab";
  onCancel?: () => void;
  onSaved?: () => void;
}

export function PersonProfileForm({
  user,
  activeTenantId,
  variant = "tab",
  onCancel,
  onSaved,
}: PersonProfileFormProps) {
  const { t } = useTranslation();
  const [displayName, setDisplayName] = useState(user.displayName);
  const [touched, setTouched] = useState(false);
  const updateUser = useUpdateUser();

  useEffect(() => {
    setDisplayName(user.displayName);
    setTouched(false);
  }, [user.id, user.displayName]);

  const nameError = touched ? validateDisplayName(displayName, t) : null;
  const nameUnchanged = displayName.trim() === user.displayName;
  const canSubmit =
    !!activeTenantId &&
    !nameUnchanged &&
    !validateDisplayName(displayName, t) &&
    !updateUser.isPending;

  const handleSubmit = async () => {
    setTouched(true);
    if (!canSubmit || !activeTenantId) return;
    try {
      await updateUser.mutateAsync({
        userId: user.id,
        tenantId: activeTenantId,
        changes: { displayName: displayName.trim() },
      });
      onSaved?.();
    } catch {
      // hook shows toast
    }
  };

  const showCounter = displayName.length >= CHAR_COUNTER_THRESHOLD;
  const nearLimit = displayName.length > DISPLAY_NAME_MAX;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {variant === "dialog" ? (
        <DialogIconHeader
          icon={UserCog}
          title={t("iam.editProfile.title")}
          description={t("iam.editProfile.description")}
        />
      ) : null}

      <div className="flex flex-col gap-5 px-6 py-5">
        <div className="flex flex-col gap-1.5">
          <div className="flex items-center justify-between">
            <label className="text-xs font-medium text-foreground">
              {t("iam.editProfile.displayName")} <span className="text-destructive">*</span>
            </label>
            {showCounter && (
              <span className={cn("text-[11px] tabular-nums", nearLimit ? "text-destructive" : "text-muted-foreground")}>
                {displayName.length}/{DISPLAY_NAME_MAX}
              </span>
            )}
          </div>
          <input
            type="text"
            placeholder={t("iam.editProfile.displayNamePlaceholder")}
            value={displayName}
            onChange={(e) => { setDisplayName(e.target.value); setTouched(true); }}
            onBlur={() => setTouched(true)}
            maxLength={DISPLAY_NAME_MAX + 10}
            className={cn(
              "h-9 rounded-md border bg-background px-3 text-sm text-foreground outline-none",
              "placeholder:text-muted-foreground/60 transition-colors focus:ring-1",
              nameError
                ? "border-destructive focus:border-destructive focus:ring-destructive/40"
                : "border-border focus:border-ring focus:ring-ring/40",
            )}
          />
          {nameError && <p className="text-[11px] text-destructive">{nameError}</p>}
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-muted-foreground">{t("iam.editProfile.email")}</label>
          <p className="text-sm text-muted-foreground">{user.email}</p>
        </div>
      </div>

      <div className="mt-auto flex items-center justify-between border-t border-border px-6 py-4">
        {variant === "dialog" ? (
          <span className="text-xs text-muted-foreground">{t("iam.shared.pressEscToCancel")}</span>
        ) : <span />}
        <div className="flex items-center gap-2">
          {onCancel && (
            <Button variant="outline" size="sm" onClick={onCancel}>
              {t("common.cancel")}
            </Button>
          )}
          <Button size="sm" disabled={!canSubmit} onClick={() => void handleSubmit()}>
            {updateUser.isPending
              ? <><Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />{t("iam.editProfile.saving")}</>
              : t("iam.editProfile.save")}
          </Button>
        </div>
      </div>
    </div>
  );
}
